import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

function Assets() {
  const { hasPermission, user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    assetName: '',
    assetType: 'Laptop',
    assetId: '',
    serialNumber: '',
    description: ''
  });
  const isAdmin = hasPermission('assets', 'create');

  useEffect(() => {
    fetchAssets();
    if (isAdmin) {
      fetchEmployees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/assets', formData);
      setShowModal(false);
      setFormData({
        assetName: '',
        assetType: 'Laptop',
        assetId: '',
        serialNumber: '',
        description: ''
      });
      fetchAssets();
    } catch (error) {
      alert('Error creating asset');
    }
  };

  const handleAssign = async (assetId, employeeId) => {
    try {
      await api.put(`/assets/${assetId}`, { assignedTo: employeeId });
      fetchAssets();
    } catch (error) {
      alert('Error assigning asset');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: 'badge-success',
      assigned: 'badge-info',
      maintenance: 'badge-warning',
      retired: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Asset Management</h1>
        {hasPermission('assets', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Add Asset
          </button>
        )}
      </div>

      <div className="card">
        {isAdmin ? (
          <h3 style={{ marginBottom: '15px' }}>All Assets</h3>
        ) : (
          <h3 style={{ marginBottom: '15px' }}>My Assigned Assets</h3>
        )}
        <table className="table">
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Asset Name</th>
              <th>Type</th>
              <th>Serial Number</th>
              <th>Status</th>
              {isAdmin && <th>Assigned To</th>}
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {assets.map((asset) => (
              <tr key={asset._id}>
                <td>{asset.assetId}</td>
                <td>{asset.assetName}</td>
                <td>{asset.assetType}</td>
                <td>{asset.serialNumber || 'N/A'}</td>
                <td>
                  <span className={`badge ${getStatusBadge(asset.status)}`}>
                    {asset.status}
                  </span>
                </td>
                {isAdmin && (
                  <td>
                    {asset.assignedTo ? (
                      `${asset.assignedTo.firstName} ${asset.assignedTo.lastName} (${asset.assignedTo.employeeId})`
                    ) : (
                      'Not Assigned'
                    )}
                  </td>
                )}
                {isAdmin && (
                  <td>
                    <select
                      className="form-control"
                      value={asset.assignedTo?._id || ''}
                      onChange={(e) => handleAssign(asset._id, e.target.value || null)}
                      style={{ width: '200px' }}
                    >
                      <option value="">Unassign</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.firstName} {emp.lastName}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        
        {assets.length === 0 && (
          <p className="text-center" style={{ padding: '20px' }}>
            {isAdmin ? 'No assets available' : 'No assets assigned to you'}
          </p>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Asset</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Asset Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.assetName}
                  onChange={(e) => setFormData({ ...formData, assetName: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Asset Type</label>
                <select
                  className="form-control"
                  value={formData.assetType}
                  onChange={(e) => setFormData({ ...formData, assetType: e.target.value })}
                >
                  <option value="Laptop">Laptop</option>
                  <option value="Desktop">Desktop</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Tablet">Tablet</option>
                  <option value="Monitor">Monitor</option>
                  <option value="Keyboard">Keyboard</option>
                  <option value="Mouse">Mouse</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Asset ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.assetId}
                  onChange={(e) => setFormData({ ...formData, assetId: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Serial Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Asset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assets;
